import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, subMonths, addMonths } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { ChartSkeleton, StatsSkeleton } from './SkeletonLoaders';
import { useToast } from '../hooks/useToast';
import { formatCurrency } from '../utils/formatters';
import { useMonthlyChart } from '../hooks/useMonthlyChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MonthlyData {
  month: string;
  total: number;
  label: string;
}

interface MonthlyExpensesChartProps {
  currency: string;
  startDate?: Date | null;
  endDate?: Date | null;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
}

const MonthlyExpensesChart: React.FC<MonthlyExpensesChartProps> = ({
  currency,
  startDate: propStartDate,
  endDate: propEndDate,
  onDateRangeChange
}) => {
  const { showError } = useToast();
  const [excludeBigExpenses, setExcludeBigExpenses] = useState(false);

  // Fetch data using the hook - dates are optional (null = all-time data)
  const { monthlyChartData, monthlyChartStats, loading, error } = useMonthlyChart(
    currency,
    propStartDate ?? null,
    propEndDate ?? null,
    excludeBigExpenses
  );

  // Use the data from the hook
  const monthlyData = monthlyChartData || [];
  const stats = monthlyChartStats;

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartMonth, setTempStartMonth] = useState(propStartDate?.getMonth() || 0);
  const [tempStartYear, setTempStartYear] = useState(propStartDate?.getFullYear() || new Date().getFullYear());
  const [tempEndMonth, setTempEndMonth] = useState(propEndDate?.getMonth() || 0);
  const [tempEndYear, setTempEndYear] = useState(propEndDate?.getFullYear() || new Date().getFullYear());


  // Date picker functions
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleDatePickerApply = () => {
    const newStartDate = new Date(tempStartYear, tempStartMonth, 1);
    // Calculate end of the selected end month
    const newEndDate = new Date(tempEndYear, tempEndMonth + 1, 0);

    // Validate: start date should not be after end date
    if (newStartDate > newEndDate) {
      showError('Start date cannot be after end date');
      return;
    }

    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
    setShowDatePicker(false);
  };

  const handleDatePickerCancel = () => {
    setTempStartMonth(propStartDate?.getMonth() || 0);
    setTempStartYear(propStartDate?.getFullYear() || new Date().getFullYear());
    setTempEndMonth(propEndDate?.getMonth() || 0);
    setTempEndYear(propEndDate?.getFullYear() || new Date().getFullYear());
    setShowDatePicker(false);
  };

  const handleDatePickerOpen = () => {
    setTempStartMonth(propStartDate?.getMonth() || 0);
    setTempStartYear(propStartDate?.getFullYear() || new Date().getFullYear());
    setTempEndMonth(propEndDate?.getMonth() || 0);
    setTempEndYear(propEndDate?.getFullYear() || new Date().getFullYear());
    setShowDatePicker(true);
  };

  const goToPreviousMonth = () => {
    if (!propStartDate || !propEndDate) return;
    const newStartDate = subMonths(propStartDate, 1);
    const newEndDate = subMonths(propEndDate, 1);

    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
  };

  const goToNextMonth = () => {
    if (!propStartDate || !propEndDate) return;
    const newStartDate = addMonths(propStartDate, 1);
    const newEndDate = addMonths(propEndDate, 1);

    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
  };

  const chartData = {
    labels: monthlyData.map(d => d.label),
    datasets: [
      {
        label: 'Monthly Expenses',
        data: monthlyData.map(d => d.total),
        borderColor: '#fe3c72',
        backgroundColor: 'rgba(254, 60, 114, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#fe3c72',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false
      },
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fe3c72',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context: any) => context[0].label,
          label: (context: any) => formatCurrency(context.parsed.y, currency)
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false
        },
        grid: {
          display: false
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          }
        }
      },
      y: {
        display: true,
        title: {
          display: false
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          },
          callback: function(value: any) {
            return formatCurrency(value, currency, 0);
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    elements: {
      line: {
        borderWidth: 3,
      },
      point: {
        radius: 4,
        hoverRadius: 6
      }
    }
  };

  // Determine what to show in the chart area
  const hasData = monthlyData.length > 0;
  const showEmptyState = !loading && !error && !hasData;
  const showErrorState = !loading && error;

  return (
    <div className="monthly-chart-container">
      <div className="monthly-chart-header">
        <h2 className="monthly-chart-title">Monthly Expenses</h2>

        <div className="chart-controls-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={excludeBigExpenses}
              onChange={(e) => setExcludeBigExpenses(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Exclude big expenses</span>
          </label>

          <div className="chart-date-controls">
          <button
            onClick={goToPreviousMonth}
            className="btn btn-icon btn-secondary"
            title="Previous month"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="chart-current-period" onClick={handleDatePickerOpen} style={{ cursor: 'pointer' }}>
            <Calendar size={14} />
            <span className="chart-period-text">
              {propStartDate && propEndDate ? `${format(propStartDate, 'MMM yyyy')} - ${format(propEndDate, 'MMM yyyy')}` : 'All Time'}
            </span>
          </div>

          <button
            onClick={goToNextMonth}
            className="btn btn-icon btn-secondary"
            title="Next month"
          >
            <ChevronRight size={16} />
          </button>
          </div>
        </div>
      </div>

      {/* Date Picker Dropdown */}
      {showDatePicker && (
        <>
          <div className="datepicker-backdrop" onClick={handleDatePickerCancel}></div>
          <div className="chart-datepicker-popup">
            <div className="datepicker-header">
              <div className="datepicker-section">
                <label>Start Date:</label>
                <div className="datepicker-controls">
                  <select
                    className="datepicker-select"
                    value={tempStartMonth}
                    onChange={(e) => setTempStartMonth(parseInt(e.target.value))}
                  >
                    {monthNames.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="datepicker-select"
                    value={tempStartYear}
                    onChange={(e) => setTempStartYear(parseInt(e.target.value))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="datepicker-section">
                <label>End Date:</label>
                <div className="datepicker-controls">
                  <select
                    className="datepicker-select"
                    value={tempEndMonth}
                    onChange={(e) => setTempEndMonth(parseInt(e.target.value))}
                  >
                    {monthNames.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="datepicker-select"
                    value={tempEndYear}
                    onChange={(e) => setTempEndYear(parseInt(e.target.value))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="datepicker-footer">
              <button
                type="button"
                onClick={handleDatePickerCancel}
                className="datepicker-btn datepicker-btn-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDatePickerApply}
                className="datepicker-btn datepicker-btn-apply"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      <div className="monthly-chart-content">
        {/* Loading state */}
        {loading && (
          <>
            <div className="monthly-chart-wrapper">
              <ChartSkeleton />
            </div>
            <div className="monthly-chart-stats">
              <StatsSkeleton count={3} />
            </div>
          </>
        )}

        {/* Error state */}
        {showErrorState && (
          <div className="monthly-chart-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
            <div style={{ textAlign: 'center', color: '#ff4444' }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>Failed to load data</p>
              <p style={{ fontSize: '14px', color: '#999' }}>{error?.message || 'Please try again or adjust date range'}</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {showEmptyState && (
          <div className="monthly-chart-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
            <div style={{ textAlign: 'center', color: '#999' }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>No expenses yet</p>
              <p style={{ fontSize: '14px' }}>Start tracking your expenses to see monthly trends</p>
            </div>
          </div>
        )}

        {/* Chart with data */}
        {hasData && !loading && (
          <>
            <div className="monthly-chart-wrapper">
              <Line data={chartData} options={chartOptions} />
            </div>
            <div className="monthly-chart-stats">
              <div className="chart-stat">
                <span className="chart-stat-label">Highest Month</span>
                <span className="chart-stat-value">
                  {stats ? formatCurrency(stats.highest_month, currency) : '0,00'}
                </span>
              </div>
              <div className="chart-stat">
                <span className="chart-stat-label">Average</span>
                <span className="chart-stat-value">
                  {stats ? formatCurrency(stats.average_monthly, currency) : '0,00'}
                </span>
              </div>
              <div className="chart-stat">
                <span className="chart-stat-label">Total ({monthlyData.length} months)</span>
                <span className="chart-stat-value">
                  {stats ? formatCurrency(stats.total_amount, currency) : '0,00'}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlyExpensesChart;
